import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import type { DashboardVM } from '../../lib/compute';

// A full refresh (Matomo + Amazon + Woo + OTTO + ad-spend → KV) typically takes
// ~11 min. We can't show true step-progress without extra backend writes, so the
// bar is an elapsed/ETA estimate; completion is detected for real by polling
// GET /api/refresh and waiting for refresh_meta.finishedAt to pass our request time.
const EST_SECONDS = 11 * 60;
const POLL_MS = 8000;
const TIMEOUT_MS = 22 * 60 * 1000;

type Phase = 'idle' | 'busy' | 'running' | 'done' | 'error' | 'timeout';
type Meta = { status?: string; finishedAt?: string; runUrl?: string };

function clock(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export function Daten({ vm }: { vm: DashboardVM }) {
  const { isAdmin, authedFetch, devMode } = useAuth();
  const [phase, setPhase] = useState<Phase>('idle');
  const [msg, setMsg] = useState('');
  const [elapsed, setElapsed] = useState(0); // seconds since dispatch
  const [finishedAt, setFinishedAt] = useState<string | undefined>();
  const [runUrl, setRunUrl] = useState<string | undefined>();

  const reqAtRef = useRef('');
  const startRef = useRef(0);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  const stop = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };
  useEffect(() => stop, []);

  const poll = async () => {
    try {
      const res = await authedFetch('/api/refresh');
      if (!res.ok) return;
      const { meta } = (await res.json()) as { meta?: Meta };
      // Done only once the finished run is newer than the one we just triggered.
      if (meta?.finishedAt && new Date(meta.finishedAt) > new Date(reqAtRef.current)) {
        stop();
        if (meta.status === 'ok') {
          setFinishedAt(meta.finishedAt);
          setPhase('done');
          setMsg('Aktualisierung abgeschlossen — Daten werden neu geladen …');
          window.setTimeout(() => window.location.reload(), 1800);
        } else {
          setRunUrl(meta.runUrl);
          setPhase('error');
          setMsg('Aktualisierung fehlgeschlagen.');
        }
      }
    } catch {
      /* transient poll error — keep polling */
    }
  };

  // Track a dispatched run. Started from the button, or on mount when a run requested
  // earlier is still pending — the job runs on GitHub, so leaving this tab never
  // interrupts it; coming back simply resumes the progress view.
  const startTracking = (requestedAt: string) => {
    stop();
    reqAtRef.current = requestedAt;
    startRef.current = new Date(requestedAt).getTime();
    setElapsed(Math.max(0, Math.round((Date.now() - startRef.current) / 1000)));
    setPhase('running');
    tickRef.current = window.setInterval(() => {
      setElapsed(Math.round((Date.now() - startRef.current) / 1000));
      if (Date.now() - startRef.current > TIMEOUT_MS) {
        stop();
        setPhase('timeout');
      }
    }, 1000);
    pollRef.current = window.setInterval(poll, POLL_MS);
    window.setTimeout(poll, 3000); // first check shortly after dispatch
  };

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/refresh');
        if (!res.ok || cancelled) return;
        const { request, meta } = (await res.json()) as { request?: { at?: string }; meta?: Meta };
        const at = request?.at;
        if (!at) return;
        const pending = !meta?.finishedAt || new Date(meta.finishedAt) <= new Date(at);
        if (pending && Date.now() - new Date(at).getTime() < TIMEOUT_MS) startTracking(at);
      } catch {
        /* status unknown — show idle */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const triggerRefresh = async () => {
    stop();
    setPhase('busy');
    setMsg('');
    setElapsed(0);
    setFinishedAt(undefined);
    setRunUrl(undefined);
    try {
      const res = await authedFetch('/api/refresh', { method: 'POST' });
      const body = (await res.json().catch(() => ({}))) as { requestedAt?: string; error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);

      startTracking(body.requestedAt || new Date().toISOString());
    } catch (e) {
      setPhase('error');
      setMsg(e instanceof Error ? e.message : 'Fehlgeschlagen.');
    }
  };

  const busy = phase === 'busy' || phase === 'running';
  const pct = phase === 'done' ? 100 : Math.min(96, Math.round((elapsed / EST_SECONDS) * 100));
  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  const remain = Math.max(0, EST_SECONDS - elapsed);
  const etaTxt = remain > 60 ? `noch ca. ${Math.ceil(remain / 60)} Min.` : 'fast fertig …';

  const btnLabel =
    phase === 'busy' ? 'Wird angefordert …' : phase === 'running' ? `Läuft … ${mmss}` : 'Jetzt aktualisieren';

  return (
    <>
      <div className="cw-pagehead">
        <h1>Daten &amp; Quellen</h1>
        <span className="meta">Letzter erfolgreicher Import je Quelle</span>
      </div>

      <section className="cw-card cw-refresh-bar">
        <div className="cw-refresh-info">
          <div className="cw-refresh-when">
            <span className="lbl">Letzte Aktualisierung</span>
            <span className="val cw-num">{vm.refreshedAt}</span>
          </div>
          <div className="cw-refresh-sched">Automatische Aktualisierung täglich um 07:00 Uhr (CET)</div>
        </div>
        {isAdmin && (
          <div className="cw-refresh-action">
            <button className="cw-refresh-btn" onClick={triggerRefresh} disabled={busy}>
              {btnLabel}
            </button>
          </div>
        )}
      </section>

      {phase === 'running' && (
        <div className="cw-refresh-prog">
          <div className="bar">
            <i style={{ width: `${Math.max(4, pct)}%` }} />
          </div>
          <div className="lbl">
            <span className="cw-spin" /> Läuft seit {mmss} · {etaTxt}{' '}
            <span style={{ color: '#B4AFA8' }}>(geschätzt — Abschluss wird automatisch erkannt)</span>
          </div>
        </div>
      )}
      {phase === 'done' && (
        <div className="cw-refresh-prog ok">
          <div className="bar">
            <i style={{ width: '100%' }} />
          </div>
          <div className="lbl">✓ Fertig um {clock(finishedAt)} Uhr — {msg}</div>
        </div>
      )}
      {phase === 'error' && (
        <div className="cw-roas-note warn">
          {msg}
          {runUrl && (
            <>
              {' '}
              <a href={runUrl} target="_blank" rel="noreferrer">
                Log ansehen
              </a>
            </>
          )}
        </div>
      )}
      {phase === 'timeout' && (
        <div className="cw-roas-note warn">
          Die Aktualisierung dauert länger als üblich. Sie läuft im Hintergrund weiter — lade die Seite in ein paar
          Minuten neu, um das Ergebnis zu sehen.
        </div>
      )}
      {devMode && (
        <div className="cw-modal-note" style={{ marginBottom: 16 }}>
          Dev-Modus: der Aktualisieren-Button wirkt nur in der bereitgestellten App.
        </div>
      )}

      <section className="cw-card" style={{ padding: '8px 24px 12px', marginBottom: 20 }}>
        <div className="cw-src-grid cw-src-head">
          <span>Quelle</span>
          <span>Status</span>
          <span>Letzter Import</span>
          <span>Details</span>
        </div>
        {vm.imports.map((im) => (
          <div key={im.source} className="cw-src-grid cw-src-row">
            <span className="cw-src-name">{im.source}</span>
            <span>
              <span className={`cw-src-status ${im.status}`}>
                <span className="dot" /> {im.statusLabel}
              </span>
            </span>
            <span className="cw-num cw-src-time">{im.lastSuccess}</span>
            <span className="cw-src-detail">{im.detail}</span>
          </div>
        ))}
      </section>

      <div className="cw-sources">{vm.sources}</div>
    </>
  );
}
